import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";
import { Device } from "@twilio/voice-sdk";
import { PropTypes } from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import Draggable from "react-draggable";
import Tooltipable from "~/components/effects/tooltipable";
import GlobalContainer from "~/components/global_container";
import { NOOP, NOOP_FALSE } from "~/constants/callbacks";
import { BUTTONS } from "~/constants/DigitButtons";
import AppDispatcher from "~/dispatchers/app_dispatcher";
import { captureEvent } from "~/helpers/capture_posthog_event";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import LeadHelpers from "~/helpers/lead_helpers";
import { createCallQualityCollector } from "~/helpers/twilio_quality_instrumentation";
import APIRequest from "~/lib/api_request";
import { apiFetch } from "~/lib/fetchClient";
import { voicemailDropsApi } from "~/pages/UserSettings/api/voicemailDropsApi";
import { voicemailRecordingsApi } from "~/pages/UserSettings/api/voicemailRecordingsApi";
import AppModal from "./app_modal";

const DEVICE_OPTIONS = {
  codecPreferences: ["opus", "pcmu"],
  edge: ["ashburn", "umatilla", "roaming"],
  enableImprovedSignalingErrorPrecision: true,
};

// Feature flag for the BK-581 voicemail-drop control. No SHAKAPACKER_PUBLIC_*
// env var exists yet (matches PR 4 VoicemailDropSettings), so use the literal
// flag key.
const VOICEMAIL_DROP_FLAG = "voicemail_drop_enabled";

// Calling-SCOPE products that may use Drop VM (BK-581 supports recruiting and
// retention callers who already have normal calling access).
// GlobalContainer.product() is purely path-based and returns exactly one of:
//   'recruiting' (/recruiter), 'agent' (/agent dashboard — loads the RECRUITING
//   table and is recruiting-colored), 'retention' (/retention), 'archived'
//   (/recruiter/archived or /archived), or 'default' (non-product pages).
// There is NO affiliate/external value here, so this can't broaden beyond the
// PRD; affiliate/external users are excluded by Rails.abilities.manageLead.
// We allow only live calling contexts. 'archived'/'default' are not normal
// calling contexts and stay excluded.
const VOICEMAIL_DROP_SCOPE_PRODUCTS = ["recruiting", "retention", "agent"];

// Exact copy (PRD Requirement 2 + child spec).
const VM_COPY = {
  beepTooltip: "Click after you hear the voicemail beep.",
  noRecordings: "Add a voicemail drop in User Settings.",
  preconnect: "Drop VM is available after the call connects.",
  dropping: "Dropping...",
  accepted:
    "Voicemail drop started. Use the X in the top right to close this dialer and move to the next contact.",
  failure:
    "Could not drop voicemail. You can keep talking or hang up normally.",
  playingHeader: "Voicemail playing...",
  callerDisconnected: "Caller disconnected",
};

const SETTINGS_LINK = "/users/edit?tab=general_settings";

// Format a recording's duration_seconds as m:ss for the dropdown rows.
const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

// PostHog recording_count_bucket from the recordings count.
const recordingCountBucket = (count) => {
  if (count >= 6) {
    return "6_plus";
  }
  if (count >= 2) {
    return "2_5";
  }
  return "1";
};

// PostHog call_duration_bucket from the call timer (ms). Exclusive upper
// boundaries so each second maps to exactly one bucket.
const callDurationBucket = (ms) => {
  const seconds = Math.floor((ms || 0) / 1000);
  if (seconds < 30) {
    return "under_30s";
  }
  if (seconds < 60) {
    return "30_60s";
  }
  if (seconds < 180) {
    return "1_3m";
  }
  return "over_3m";
};

// Map a server failure message -> PostHog failure_reason. The exact server
// strings are the CONFIRMED API contract (PR 3 #859).
const failureReasonFromMessage = (message) => {
  const msg = (message || "").toLowerCase();
  if (msg.includes("not ready")) {
    return "not_connected";
  }
  if (msg.includes("no longer connected") || msg.includes("not found")) {
    return "not_connected";
  }
  if (msg.includes("unavailable") || msg.includes("no voicemail recording")) {
    return "recording_unavailable";
  }
  if (msg.includes("storage is not configured")) {
    return "twilio_rejected";
  }
  if (msg.includes("already in progress")) {
    return "twilio_rejected";
  }
  if (msg.includes("permission") || msg.includes("access")) {
    return "unauthorized";
  }
  return "unknown";
};

// Disconnect cause taxonomy emitted to PostHog `call_ended` events.
// These literals ARE the public contract to analytics dashboards — keep
// values stable. ERROR is the base; specific Twilio errors append `:CODE`.
const DISCONNECT_CAUSES = {
  DIALER_CLOSED: "dialer-closed",
  USER_HANGUP: "user-hangup",
  SDK_DISCONNECT: "sdk-disconnect",
  REMOTE_DISCONNECT: "remote-disconnect",
  // BK-581: caller/drop-initiated teardown after Twilio accepts a voicemail
  // drop — NOT a prospect hangup. Distinct so analytics don't miscount it as
  // a remote disconnect.
  DROP_DISCONNECT: "drop-disconnect",
  ERROR: "error",
};

const REQUESTED_EDGE = (DEVICE_OPTIONS.edge || []).join(",");

const NETWORK_WARNING_NAMES = new Set([
  "high-rtt",
  "high-jitter",
  "high-packet-loss",
  "high-packets-lost-fraction",
  "low-mos",
  "low-bytes-received",
  "low-bytes-sent",
]);

const INPUT_WARNING_NAMES = new Set(["constant-audio-input-level"]);

const OUTPUT_WARNING_NAMES = new Set(["constant-audio-output-level"]);

let pendingActivityRequest = null;

async function getAccessToken() {
  try {
    const data = await apiFetch.get("/twilio/token", { prefix: "" });
    return data.token;
  } catch (error) {
    console.error("Error fetching access token:", error);
    throw new Error(
      "Could not fetch token, please reload the page and try again",
    );
  }
}

class Dialer extends React.Component {
  _isMounted = false;

  connection = null;

  constructor(props) {
    super(props);

    const { currentUser, currentTeam } = Rails.helpers;
    const smsNumber = _lodash.first(currentUser.sms_numbers);

    this.state = {
      body: "",
      teamId: currentTeam.id,
      userId: currentUser.id,
      muted: false,
      hold: false,
      callLog: "Call Initialization",
      onPhone: false,
      userPhoneNumber: smsNumber ? smsNumber.service_number : null,
      recordCall: currentUser.role ? currentUser.role.record_calls : false,
      disconnectionHandled: false,
      error: "",
      initialized: false,
      timerOn: false,
      timerStart: 0,
      timerTime: 0,
      creatingActivity: false,
      deviceType: this.detectDeviceType(),
      dialAttemptId: null,
      twilioFeedback: null,
      twilioFeedbackLevel: "info",
      callAnswered: false,
      // BK-581 voicemail-drop state.
      flagEnabled:
        typeof window !== "undefined" && window.isPosthogInitialized
          ? checkFeatureFlag(VOICEMAIL_DROP_FLAG)
          : false,
      vmRecordings: [],
      vmRecordingsLoaded: false,
      dropStatus: "idle", // 'idle' | 'dropping' | 'accepted' | 'failed'
      dropError: "",
      vmDropdownOpen: false,
      // True only once the Twilio call is ANSWERED (the 'accept' event), false
      // before accept (Pending/Calling/Ringing) and after disconnect/call-end.
      // Gates Drop VM so it never appears on a ringing call (endpoint rejects
      // "not ready") as an active control.
      callConnected: false,
    };

    this.qualityCollector = null;
    this.currentDialAttemptId = null;
    this.activityCreationAttempted = false;
    // True between device.connect() being called and the Call object being
    // assigned to this.connection. Guards against a second connect attempt
    // triggered by a re-registration event (e.g. after a TransportError 31009)
    // before the first call's promise has resolved.
    this.isConnecting = false;
    this.lastUiAction = null;
    this.modalHideObserved = false;

    // Set BEFORE disconnecting on an accepted drop so handleCallEnd /
    // handleDisconnection skip activity creation (backend already wrote it).
    this.voicemailDropAccepted = false;

    // True between submitDrop's request start and its definitive outcome.
    // While set, the disconnect handlers MUST NOT create a phone-call activity
    // — the drop may resolve 'accepted' (backend owns the activity). If the
    // call ends mid-drop we defer the decision until the outcome is known.
    this.voicemailDropPending = false;

    // Set when the call ends WHILE a drop is in flight. We snapshot the data
    // createActivityAfterDisconnection needs (the connection is nulled right
    // after), so a later FAILED outcome can still log the call from the
    // snapshot — neither browser nor backend logged it otherwise.
    this.disconnectDeferredDuringDrop = false;
    this.deferredActivitySnapshot = null;

    // Set right before handleDropAccepted calls connection.disconnect() so the
    // resulting disconnect event reports DROP_DISCONNECT (caller/drop-initiated)
    // rather than REMOTE_DISCONNECT (prospect hangup).
    this.dropInitiatedDisconnect = false;

    // If Twilio disconnects AFTER Drop VM is clicked but BEFORE the drop POST
    // resolves, the cause is ambiguous: an accepted drop should log
    // DROP_DISCONNECT, a failed one the normal remote/sdk cause. We DEFER the
    // quality emit (keep the collector attached) and let the drop resolution
    // emit it. These hold the deferral state until then.
    this.qualityCauseDeferred = false;
    this.deferredQualityCause = null;
    this.voicemailRecordingsLoadSeq = 0;
    this.voicemailRecordingsLoadPromise = null;

    autoBind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.setLastUiAction("dialer_opened");
    this.emitTwilioDialerEvent("dialer_opened");
    this.initializeDevice();

    // Make the feature flag reactive to PostHog readiness (same pattern as
    // PR 4 VoicemailDropSettings / Notifications/Button.jsx, adapted to a
    // class component). Seed synchronously above when PostHog is already up.
    if (typeof window !== "undefined") {
      if (window.isPosthogInitialized) {
        this.handlePosthogReady();
      } else {
        window.addEventListener("posthogReady", this.handlePosthogReady);
      }
    }

    // Prefetch the caller's recordings for the modal session so the Drop VM
    // control reflects the correct state (none / one / multiple) the moment
    // the call connects.
    this.loadVoicemailRecordings();
  }

  componentWillUnmount() {
    const { onDialerClose } = this.props;
    this._isMounted = false;
    if (this.hasActiveCall()) {
      this.emitTwilioDialerEvent("modal_unmounted_active_call", {
        modal_hide_observed: this.modalHideObserved,
        timer_ms: this.state.timerTime,
      });
    } else if (this.modalHideObserved) {
      this.emitTwilioDialerEvent("dialer_unmounted", {
        modal_hide_observed: true,
      });
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("posthogReady", this.handlePosthogReady);
    }
    if (this.qualityCollector) {
      // Choose the still-pending cause when closing the dialer:
      //  - accepted-drop teardown whose async disconnect never fired => DROP_DISCONNECT
      //  - a disconnect that fired mid-drop but the drop never resolved =>
      //    its deferred base cause (remote/sdk)
      //  - otherwise the plain close
      let cause = DISCONNECT_CAUSES.DIALER_CLOSED;
      if (this.dropInitiatedDisconnect) {
        cause = DISCONNECT_CAUSES.DROP_DISCONNECT;
      } else if (this.qualityCauseDeferred) {
        cause = this.deferredQualityCause;
      }
      this.dropInitiatedDisconnect = false;
      this.qualityCauseDeferred = false;
      this.deferredQualityCause = null;
      this.emitCallQualityEvent(cause);
    }
    if (this.device) {
      this.device.destroy();
    }
    onDialerClose();
  }

  generateDialAttemptId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    return `dial-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  setTwilioFeedback(message, level = "info") {
    this.setState({
      twilioFeedback: message,
      twilioFeedbackLevel: level,
    });
  }

  setLastUiAction(action, properties = {}) {
    this.lastUiAction = {
      action,
      at: Date.now(),
      ...properties,
    };
  }

  getCallStatus() {
    if (!this.connection || typeof this.connection.status !== "function") {
      return null;
    }

    try {
      return this.connection.status();
    } catch (error) {
      return null;
    }
  }

  hasActiveCall() {
    return !!(this.connection || this.qualityCollector || this.state.onPhone);
  }

  dialerEventContext() {
    const { currentForm, phoneSlot } = this.props;
    const { onPhone, callAnswered } = this.state;

    return {
      active_call: this.hasActiveCall(),
      on_phone: onPhone,
      call_answered: callAnswered,
      call_status_at_event: this.getCallStatus(),
      current_form: currentForm,
      phone_slot: phoneSlot,
      last_ui_action: this.lastUiAction?.action || null,
    };
  }

  handleTwilioQualityWarning(name) {
    if (INPUT_WARNING_NAMES.has(name)) {
      this.setTwilioFeedback(
        "Microphone issue detected. Check your input device.",
        "warning",
      );
      return;
    }

    if (OUTPUT_WARNING_NAMES.has(name)) {
      this.setTwilioFeedback(
        "Speaker issue detected. Check your output device.",
        "warning",
      );
      return;
    }

    if (NETWORK_WARNING_NAMES.has(name)) {
      this.setTwilioFeedback(
        "Network is unstable. Check your internet connection.",
        "warning",
      );
      return;
    }

    this.setTwilioFeedback("Call quality degraded.", "warning");
  }

  handleTwilioQualityWarningCleared(_name, activeWarnings) {
    if (activeWarnings.length === 0) {
      this.setTwilioFeedback("Call quality restored.", "info");
    }
  }

  emitTwilioDialerEvent(eventType, properties = {}) {
    const { teamId, userId, dialAttemptId } = this.state;
    const { lead } = this.props;

    captureEvent("twilio_dialer_event", {
      event_type: eventType,
      dial_attempt_id: this.currentDialAttemptId || dialAttemptId,
      call_sid: this.connection?.parameters?.CallSid || null,
      team_id: teamId,
      user_id: userId,
      lead_id: lead?.id || null,
      actual_edge: this.device ? this.device.edge : null,
      ...this.dialerEventContext(),
      ...properties,
    });
  }

  twilioErrorProps(error) {
    return {
      twilio_error_code: error?.code || null,
      twilio_error_message: error?.message || null,
    };
  }

  captureTwilioDiagnostic(error, context, properties = {}) {
    const twilioError =
      error instanceof Error ? error : new Error(error?.message || context);

    Sentry.captureException(twilioError, {
      tags: {
        feature: "twilio_dialer",
        twilio_context: context,
      },
      extra: {
        dial_attempt_id: this.currentDialAttemptId || this.state.dialAttemptId,
        call_sid: this.connection?.parameters?.CallSid || null,
        team_id: this.state.teamId,
        user_id: this.state.userId,
        lead_id: this.props.lead?.id || null,
        ...this.twilioErrorProps(error),
        ...properties,
      },
    });
  }

  handleModalHide() {
    this.modalHideObserved = true;
    this.setLastUiAction("bootstrap_modal_hide");
    if (this.hasActiveCall()) {
      this.emitTwilioDialerEvent("modal_hide_requested", {
        modal_hide_observed: true,
      });
    }
  }

  handlePosthogReady = () => {
    if (!this._isMounted) {
      return;
    }
    this.setState(
      { flagEnabled: checkFeatureFlag(VOICEMAIL_DROP_FLAG) },
      () => {
        // If PostHog readiness flips eligibility true DURING an already-answered
        // call, the mount-time and accept-time fetches were both skipped (flag was
        // false then), so vmRecordingsLoaded is still false and Drop VM would never
        // render for this eligible user. Load now that the flag has resolved.
        // voicemailDropEligible() reads checkFeatureFlag synchronously (already
        // committed here), so it reflects the just-updated value.
        if (
          this.voicemailDropEligible() &&
          this.state.callConnected &&
          !this.state.vmRecordingsLoaded
        ) {
          this.loadVoicemailRecordings();
        }
      },
    );
  };

  // Fetch + cache the caller's voicemail recordings (default-first) for the
  // modal session. Only fetch for the eligible cohort: GET /voicemail_recordings
  // enforces the rollout/permission gate and returns 403 otherwise, which would
  // trip apiFetch's global permission-error toast for a feature the user can't
  // even see. Visibility is further gated by the live-call state at render.
  async loadVoicemailRecordings({ retryConnectedFailure = true } = {}) {
    if (!this.voicemailDropEligible()) {
      return;
    }
    if (this.voicemailRecordingsLoadPromise) {
      return this.voicemailRecordingsLoadPromise;
    }
    this.voicemailRecordingsLoadSeq += 1;
    const loadSeq = this.voicemailRecordingsLoadSeq;
    const loadPromise = (async () => {
      let retryAfterConnectedFailure = false;
      try {
        const data = await voicemailRecordingsApi.list();
        if (!this._isMounted || loadSeq !== this.voicemailRecordingsLoadSeq) {
          return;
        }
        const recordings = (Array.isArray(data) ? data : [])
          .slice()
          .sort((a, b) => {
            if (a.is_default !== b.is_default) {
              return a.is_default ? -1 : 1;
            }
            return 0;
          });
        this.setState({ vmRecordings: recordings, vmRecordingsLoaded: true });
      } catch (error) {
        if (!this._isMounted || loadSeq !== this.voicemailRecordingsLoadSeq) {
          return;
        }
        // Keep an initial load failure retryable. A transient GET failure is
        // not the same as a real empty recordings list; only preserve a
        // previously loaded successful result if a later refresh fails.
        retryAfterConnectedFailure =
          retryConnectedFailure &&
          !this.state.vmRecordingsLoaded &&
          this.state.callConnected &&
          this.voicemailDropEligible();
        this.setState((prev) => ({
          vmRecordings: prev.vmRecordings,
          vmRecordingsLoaded: prev.vmRecordingsLoaded,
        }));
      } finally {
        if (this.voicemailRecordingsLoadPromise === loadPromise) {
          this.voicemailRecordingsLoadPromise = null;
        }
        if (
          retryAfterConnectedFailure &&
          this._isMounted &&
          this.state.callConnected &&
          !this.state.vmRecordingsLoaded
        ) {
          this.loadVoicemailRecordings({ retryConnectedFailure: false });
        }
      }
    })();
    this.voicemailRecordingsLoadPromise = loadPromise;
    return loadPromise;
  }

  // Initialize state and Twilio connection with voice-sdk 2.0
  handleTwilioEvents = () => {
    const { userPhoneNumber, recordCall, userId, teamId } = this.state;
    const { lead, leadPhone } = this.props;
    const handleStateChange = (updatedState) => this.setState(updatedState);

    // Event: Device is ready
    this.device.on(Device.EventName.Registered, () => {
      this.emitTwilioDialerEvent("device_registered");
      handleStateChange({
        callLog: userPhoneNumber ? "Pending..." : "",
        onPhone: !!userPhoneNumber,
        initialized: true,
        error: "",
      });

      if (userPhoneNumber && !this.connection && !this.isConnecting) {
        this.initiateConnection(
          leadPhone,
          userPhoneNumber,
          recordCall,
          userId,
          teamId,
          lead.id,
        );
        if (this.connection) {
          this.handleConnectionEvents(this.connection);
        }
      }
    });

    // Event: Device goes offline (new SDK's offline event)
    this.device.on(Device.EventName.Unregistered, () => {
      this.emitTwilioDialerEvent("device_unregistered");
      this.handleOfflineState(handleStateChange);
    });

    // Event: Connection disconnects
    this.device.on(
      Device.EventName.Disconnect,
      this.handleDisconnection.bind(this),
    );

    // Event: Connection error
    this.device.on(Device.EventName.Error, (error) => {
      console.error("Device Error:", error);
      this.emitTwilioDialerEvent("device_error", this.twilioErrorProps(error));
      this.handleConnectionError(error, "device_error");
    });
  };

  // handleCallEnd: Ensures disconnectionHandled is set once and clears connection listeners
  handleCallEnd() {
    if (this.disconnectionHandled) {
      return;
    }
    this.disconnectionHandled = true;
    const shouldCreateActivity = this.connection && this.connection.parameters;
    if (
      shouldCreateActivity &&
      !this.voicemailDropAccepted &&
      !this.voicemailDropPending
    ) {
      this.activityCreationAttempted = true;
    }

    // Defer the quality cause if a drop is still resolving (the connection
    // disconnect handler routes here); otherwise this is the manual-hangup path
    // and USER_HANGUP is emitted immediately.
    this.emitOrDeferQualityCause(DISCONNECT_CAUSES.USER_HANGUP);

    // Clear the answered gate on every call-end path (not just the SDK
    // disconnect handler) so a stale callConnected=true can't re-enable Drop VM
    // on the next connection object before its `accept` fires.
    this.setState({ onPhone: false, callConnected: false });

    if (this.connection && this.connection.parameters) {
      const callSid = this.connection.parameters.CallSid;

      if (pendingActivityRequest) {
        APIRequest.abort(pendingActivityRequest);
      }

      if (this.voicemailDropAccepted) {
        // Accepted drop: the backend already created the phone-call activity,
        // so skip browser-side creation to avoid a duplicate.
      } else if (this.voicemailDropPending) {
        // The call ended while a drop is still in flight and the outcome is
        // unknown. Defer: snapshot what createActivityAfterDisconnection needs
        // (connection is nulled below) so handleDropFailed can log the call if
        // the drop ultimately fails. If it succeeds, the backend owns it.
        this.disconnectDeferredDuringDrop = true;
        this.deferredActivitySnapshot = {
          callSid,
          callStatus: this.connection.status(),
        };
      } else {
        this.createActivityAfterDisconnection();
      }
      this.connection.parameters = {
        ...this.connection.parameters,
        activityCreated: true,
      };
    } else {
      console.warn(
        "No valid connection parameters available in handleCallEnd.",
      );
    }

    if (this.device) {
      this.device.disconnectAll();
      this.device.removeAllListeners();
    }

    this.connection = null;
  }

  handleDialerCloseClick(event) {
    const { onPhone } = this.state;

    this.setLastUiAction("dialer_close_clicked");
    this.emitTwilioDialerEvent("dialer_close_clicked", {
      active_call: onPhone,
    });

    if (!onPhone) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.handleCallEnd();
    this.dismissDialerModal();
  }

  dismissDialerModal() {
    const $modal = this.appModal?.modal ? $(this.appModal.modal) : null;
    if (!$modal) {
      return;
    }

    this.setLastUiAction("activity_modal_hide_requested");
    $modal.modal("hide");
  }

  handleDisconnection(connection) {
    if (!this._isMounted || this.disconnectionHandled) {
      return;
    }
    this.disconnectionHandled = true;

    // handleDropAccepted calls device.disconnectAll() too, so the DEVICE-level
    // disconnect may win the race against the Call-level handler. Consume the
    // drop flag here as well so an accepted (post-resolve) drop is reported as
    // DROP_DISCONNECT. If a drop is still PENDING, DEFER the cause so the
    // resolution decides it (accepted => DROP_DISCONNECT; failed =>
    // SDK_DISCONNECT) instead of prematurely logging an sdk-disconnect.
    if (this.dropInitiatedDisconnect) {
      this.dropInitiatedDisconnect = false;
      this.emitCallQualityEvent(DISCONNECT_CAUSES.DROP_DISCONNECT);
    } else {
      this.emitOrDeferQualityCause(DISCONNECT_CAUSES.SDK_DISCONNECT);
    }

    this.setState({
      callLog: "Call Ended...",
      onPhone: false,
      callConnected: false,
      twilioFeedback: "Call ended. Start a new call if needed.",
      twilioFeedbackLevel: "info",
      initialized: false, // Setting to false if in disconnected state
    });
    this.stopTimer();
    if (
      connection &&
      connection._isAnswered &&
      !connection.parameters?.activityCreated &&
      !this.voicemailDropAccepted
    ) {
      if (!this.voicemailDropPending) {
        this.activityCreationAttempted = true;
      }
      if (this.voicemailDropPending) {
        // Call ended mid-drop, outcome unknown. Defer activity creation to the
        // drop outcome (accepted => backend logs it; failed => handleDropFailed
        // logs from this snapshot). connection may be nulled before then.
        this.disconnectDeferredDuringDrop = true;
        this.deferredActivitySnapshot = {
          callSid: connection.parameters?.CallSid,
          callStatus:
            typeof connection.status === "function"
              ? connection.status()
              : null,
        };
      } else {
        this.createActivityAfterDisconnection();
      }
      connection.parameters = {
        ...connection.parameters,
        activityCreated: true,
      };
    }

    if (connection) {
      connection.removeAllListeners();
    }
  }

  // Handle the Twilio connection error
  handleConnectionError(error, context = "connection_error") {
    this.stopTimer();
    this.qualityCollector?.setSdkError(error);
    this.captureTwilioDiagnostic(error, context);
    this.setState({
      error: error.message,
      onPhone: false,
      callConnected: false,
      twilioFeedback: "Call failed. Start a new call if needed.",
      twilioFeedbackLevel: "danger",
      initialized: true,
    });
  }

  // Handle the Twilio offline state
  handleOfflineState(handleStateChange) {
    const { connection } = this;

    this.stopTimer();
    handleStateChange({
      callLog: "Offline...",
      onPhone: false,
      callConnected: false,
      twilioFeedback:
        "Dialer connection is offline. Check your internet connection.",
      twilioFeedbackLevel: "warning",
      initialized: true,
    });

    if (connection && !connection.parameters.activityCreated) {
      if (!this.voicemailDropAccepted && !this.voicemailDropPending) {
        this.activityCreationAttempted = true;
      }
      if (this.voicemailDropAccepted) {
        // Accepted drop: the backend already created the phone-call activity,
        // so skip browser-side creation to avoid a duplicate.
      } else if (this.voicemailDropPending) {
        // Device went offline mid-drop, outcome unknown. Defer: snapshot what
        // createActivityAfterDisconnection needs so handleDropFailed can log the
        // call if the drop ultimately fails; if it succeeds the backend owns it.
        this.disconnectDeferredDuringDrop = true;
        this.deferredActivitySnapshot = {
          callSid: connection.parameters?.CallSid,
          callStatus:
            typeof connection.status === "function"
              ? connection.status()
              : null,
        };
      } else {
        this.createActivityAfterDisconnection();
      }
      this.connection.parameters = {
        ...connection.parameters,
        activityCreated: true,
      };
    }
  }

  handleConnectionEvents = (connection) => {
    connection.on("accept", () => {
      this.startTimer();

      this.setState({
        callLog: "Connected...",
      });
    });

    connection.on("ringing", () => {
      this.setState({
        callLog: "Calling...",
      });
    });

    connection.on("disconnect", () => {
      this.setState({
        callLog: "Call Ended",
        onPhone: false,
        callConnected: false,
      });
    });

    connection.on("error", (error) => {
      this.setState({
        callLog: `Error: ${error.message}`,
        onPhone: false,
        callConnected: false,
      });
    });
  };

  handleToggleCall = async (e) => {
    e.preventDefault();
    const { onPhone, userPhoneNumber, recordCall, teamId, userId } = this.state;
    const { lead, leadPhone } = this.props;
    const handleStateChange = (updatedState) => this.setState(updatedState);

    if (!onPhone && userPhoneNumber) {
      this.setLastUiAction("call_button_start_clicked");
      handleStateChange({
        muted: false,
        onPhone: true,
        error: "",
        initialized: true,
        callLog: "Pending...",
        disconnectionHandled: false,
      });

      try {
        const call = await this.initiateConnection(
          leadPhone,
          userPhoneNumber,
          recordCall,
          userId,
          teamId,
          lead.id,
        );

        if (call) {
          this.handleCallEvents(call);
        }
      } catch (error) {
        console.error("Failed to initiate call:", error);
        this.emitTwilioDialerEvent(
          "connect_failed",
          this.twilioErrorProps(error),
        );
        this.captureTwilioDiagnostic(error, "connect_failed");
        this.setState({ callLog: "Failed to connect. Please try again." });
      }
    } else if (onPhone) {
      this.setLastUiAction("hangup_button_clicked");
      this.emitTwilioDialerEvent("call_button_clicked", {
        button_action: "hangup_call",
      });
      this.handleCallEnd();
    } else {
      this.setLastUiAction("call_button_clicked_without_user_number");
      handleStateChange({ onPhone: false, initialized: true });
    }
  };

  handleToggleMute = (event) => {
    event.preventDefault();

    const { hold } = this.state;

    if (this.connection && !hold) {
      const nextMuted = !this.state.muted;
      this.setLastUiAction("mute_toggled", { muted: nextMuted });
      this.setState({ muted: nextMuted }, () => {
        const { muted } = this.state;
        try {
          this.connection.mute(muted);
        } catch (error) {
          this.captureTwilioDiagnostic(error, "mute_toggle_failed");
        }
      });
    }
  };

  handleToggleHold = (event) => {
    event.preventDefault();

    const { muted } = this.state;

    if (this.connection && !muted) {
      const nextHold = !this.state.hold;
      this.setLastUiAction("hold_toggled", { hold: nextHold });
      this.setState({ hold: nextHold }, () => {
        const { hold } = this.state;
        try {
          this.connection.mute(hold);

          const audioTrack = this.connection
            .getRemoteStream()
            .getAudioTracks()[0];

          if (audioTrack) {
            audioTrack.enabled = !hold;
          }
        } catch (error) {
          this.captureTwilioDiagnostic(error, "hold_toggle_failed");
        }
      });
    }
  };

  // --- BK-581 voicemail drop ---------------------------------------------

  // Cohort eligibility (feature flag + supported calling module + calling access) — the gate
  // that decides whether Drop VM could ever apply, independent of the live-call
  // state. Gates BOTH the recordings fetch and the control's visibility, so
  // ineligible users never hit GET /voicemail_recordings (whose 403 would
  // otherwise raise a spurious permission-error toast).
  // Reads the flag synchronously (not the reactive state copy) because the
  // fetch can run before `flagEnabled` commits; both reflect checkFeatureFlag.
  // The authoritative module signal is the LEAD, not just the dashboard product:
  // the /agent dashboard can show a referrer's recruiting AND retention leads.
  // Only those two calling modules are in BK-581 scope.
  voicemailDropEligible() {
    const lead = this.props.lead || {};
    return (
      checkFeatureFlag(VOICEMAIL_DROP_FLAG) &&
      VOICEMAIL_DROP_SCOPE_PRODUCTS.includes(GlobalContainer.product()) &&
      Rails.abilities.manageLead === true &&
      (lead.recruiting === true || lead.retention === true)
    );
  }

  voicemailDropModule() {
    const product = GlobalContainer.product();
    const lead = this.props.lead || {};
    if (
      (product === "recruiting" || product === "agent") &&
      lead.recruiting === true
    ) {
      return "recruiting";
    }
    if (product === "retention" && lead.retention === true) {
      return product;
    }
    if (lead.retention === true) {
      return "retention";
    }
    return "recruiting";
  }

  // True only for the eligible cohort while the call is live. Gates the entire
  // Drop VM control.
  voicemailDropAvailable() {
    const { onPhone, callConnected } = this.state;
    return (
      onPhone &&
      callConnected &&
      !!this.connection &&
      this.voicemailDropEligible()
    );
  }

  toggleVmDropdown() {
    this.setState((prev) => ({ vmDropdownOpen: !prev.vmDropdownOpen }));
  }

  // Drop the default recording (primary button) — recordingId omitted so the
  // server uses the caller's default. selectionMode 'default'.
  handleDropDefault(event) {
    if (event) {
      event.preventDefault();
    }
    this.submitDrop({ recordingId: null, selectionMode: "default" });
  }

  // Drop a specific recording chosen from the dropdown. selectionMode
  // 'selected'.
  handleDropRecording(recording) {
    this.setState({ vmDropdownOpen: false });
    this.submitDrop({ recordingId: recording.id, selectionMode: "selected" });
  }

  async submitDrop({ recordingId, selectionMode }) {
    const { dropStatus } = this.state;
    const { lead } = this.props;

    // Block repeat clicks while a drop is in flight or already accepted. The
    // synchronous `voicemailDropPending` flag is the authoritative guard: on a
    // fast double-click the 2nd click can enter submitDrop before React commits
    // dropStatus:'dropping', so the state check alone would let it through.
    if (
      this.voicemailDropPending ||
      dropStatus === "dropping" ||
      dropStatus === "accepted"
    ) {
      return;
    }
    if (!this.connection || !this.connection.parameters) {
      return;
    }

    const callSid = this.connection.parameters.CallSid;
    // Mark a drop in flight BEFORE the await (and before any state commit) so a
    // disconnect arriving mid-drop defers browser-side activity creation, and a
    // synchronous double-click is blocked by the guard above. This is the guard
    // against the duplicate / lost activity race.
    this.voicemailDropPending = true;
    this.setState({ dropStatus: "dropping", dropError: "" });

    try {
      const response = await voicemailDropsApi.drop(lead.id, {
        callSid,
        recordingId,
        // Persist notes typed before the drop: the accepted path suppresses
        // browser-side activity creation, so they must ride on the drop POST.
        body: this.state.body,
      });
      if (!this._isMounted) {
        return;
      }

      const status = response && response.status;
      if (status === "accepted" || status === "already_accepted") {
        this.handleDropAccepted(selectionMode);
      } else {
        // Defensive: an unexpected 2xx shape is treated as a failure so the
        // caller keeps normal controls.
        this.handleDropFailed(selectionMode, VM_COPY.failure);
      }
    } catch (error) {
      if (!this._isMounted) {
        return;
      }
      // apiFetch/parseJSON surfaces the 422 body as { errors: [...] }.
      const serverMessage = error && error.errors && error.errors[0];
      this.handleDropFailed(selectionMode, serverMessage || VM_COPY.failure);
    }
  }

  // Accepted (201): flip to the accepted state BEFORE disconnecting so the
  // accepted copy stays visible; suppress browser-side activity creation; emit
  // analytics; then tear down the browser caller without unmounting the modal.
  handleDropAccepted(selectionMode) {
    this.voicemailDropAccepted = true;
    // Backend owns the activity now; drop any deferred browser-side logging
    // that a mid-drop disconnect may have queued.
    this.voicemailDropPending = false;
    this.disconnectDeferredDuringDrop = false;
    this.deferredActivitySnapshot = null;

    this.emitDropEvent({ result: "accepted", selectionMode });

    this.stopTimer();
    this.setState({
      dropStatus: "accepted",
      dropError: "",
      muted: false,
      hold: false,
    });

    // The backend already created the phone-call activity, so do NOT create one
    // here — but the open lead drawer won't show the "Voicemail drop: {name}"
    // timeline entry until something tells it to refetch. Mirror the minimal
    // notify the normal save path uses (CREATE_LEAD_ACTIVITY_DONE drives the
    // LeadDrawerStore refetch); switch to the phone-call form so the refreshed
    // timeline is in view. Do NOT close the modal here — the accepted-state copy
    // must stay until the caller clicks X.
    this.notifyDrawerOfAcceptedDrop();

    // Disconnect the browser caller without routing through the normal
    // activity-creation/unmount path. `voicemailDropAccepted` is already set,
    // so handleCallEnd/handleDisconnection will skip createActivityAfterDisconnection.
    // Mark the teardown as drop-initiated so the disconnect handler reports
    // DROP_DISCONNECT, not REMOTE_DISCONNECT. Do NOT clear the flag here: Twilio
    // may emit the call `disconnect` event asynchronously (after disconnect()
    // returns), so the flag must survive until the disconnect handler consumes
    // it. The handler clears it once it has chosen the disconnect cause.
    this.dropInitiatedDisconnect = true;
    // If a disconnect already fired while the POST was pending, its cause was
    // deferred — flush it now as DROP_DISCONNECT (accepted wins).
    this.flushDeferredQualityCause(DISCONNECT_CAUSES.DROP_DISCONNECT);
    // Use separate try/catch so a throwing connection.disconnect() can't skip
    // the device teardown — otherwise the browser leg could stay live while the
    // UI already shows the call as disconnected.
    try {
      if (this.connection && typeof this.connection.disconnect === "function") {
        this.connection.disconnect();
      }
    } catch (e) {
      console.warn(
        "connection.disconnect failed after accepted voicemail drop:",
        e,
      );
    }
    try {
      if (this.device) {
        this.device.disconnectAll();
      }
    } catch (e) {
      console.warn(
        "device.disconnectAll failed after accepted voicemail drop:",
        e,
      );
    }
  }

  // Trigger the open lead drawer to refetch its activities so the
  // backend-created phone-call activity (with the "Voicemail drop: {name}"
  // line) appears live.
  //
  // ONLY dispatch the refetch — do NOT seed the create form via setDialerField
  // or switch to the phone-call form. The backend already logged this call;
  // seeding the create form (which keys CREATE mode off a falsy dialer.id)
  // would expose a "Log Phone Call" button that creates a DUPLICATE activity.
  // CREATE_LEAD_ACTIVITY_DONE sets LeadDrawerStore.leadDrawerStoreAction =
  // 'createLeadActivityDone', which the lead-drawer timeline listens for and
  // responds to by calling fetchActivities() — refetch without a create form.
  notifyDrawerOfAcceptedDrop() {
    AppDispatcher.dispatch({
      type: "CREATE_LEAD_ACTIVITY_DONE",
    });
  }

  // Failure before acceptance (422 / network): keep normal controls, surface
  // the server message, preserve notes, do NOT mark as a drop, allow retry.
  handleDropFailed(selectionMode, message) {
    this.voicemailDropPending = false;

    // If a disconnect raced ahead of this failed drop, its quality cause was
    // deferred — flush it now with the stored base cause (remote/sdk), since the
    // drop did NOT take over the call.
    this.flushDeferredQualityCause();

    this.emitDropEvent({
      result: "failed",
      selectionMode,
      failureReason: failureReasonFromMessage(message),
    });

    // If the call already ended during this failed drop, NEITHER the browser
    // (deferred) NOR the backend (drop rejected) logged the call. Create the
    // phone-call activity now from the snapshot so the call isn't lost.
    if (this.disconnectDeferredDuringDrop) {
      this.disconnectDeferredDuringDrop = false;
      const snapshot = this.deferredActivitySnapshot;
      this.deferredActivitySnapshot = null;
      if (snapshot) {
        this.createActivityAfterDisconnection(snapshot);
      }
    }

    this.setState({
      dropStatus: "failed",
      dropError: message || VM_COPY.failure,
    });
  }

  // Emit the single voicemail_drop_used event with the HLD §6.4 properties.
  emitDropEvent({ result, selectionMode, failureReason }) {
    const { vmRecordings, timerTime } = this.state;
    const props = {
      module: this.voicemailDropModule(),
      source: "dialer_modal",
      result,
      recording_selection_mode: selectionMode,
      recording_count_bucket: recordingCountBucket(vmRecordings.length),
      call_duration_bucket: callDurationBucket(timerTime),
    };
    if (result !== "accepted") {
      props.failure_reason = failureReason || "unknown";
    }
    captureEvent("voicemail_drop_used", props);
  }

  getDraggableConfig() {
    const { deviceType } = this.state;

    const cancelSelector = [".tablet-cancel-draggable"].join(", ");

    switch (deviceType) {
      case "mobile":
        return {
          disabled: true,
        };

      case "tablet":
        return {
          cancel: cancelSelector,
        };

      default:
        return {};
    }
  }

  startTimer = () => {
    const { timerTime } = this.state;

    this.setState(
      {
        timerOn: true,
        timerStart: Date.now() - timerTime,
      },
      () => {
        const { timerStart } = this.state;

        this.timer = setInterval(() => {
          this.setState({
            timerTime: Date.now() - timerStart,
          });
        }, 10);
      },
    );
  };

  stopTimer = () => {
    this.setState({ timerOn: false });
    clearInterval(this.timer);
  };

  handleCallEvents = (call) => {
    if (!call || typeof call.on !== "function") {
      console.error("Invalid call object provided to handleCallEvents.");
      const error = new Error(
        "Invalid call object provided to handleCallEvents.",
      );
      this.emitTwilioDialerEvent("invalid_call_object");
      this.captureTwilioDiagnostic(error, "invalid_call_object");
      this.setState({
        callLog: "Failed to handle call events. Please try again.",
      });
      return;
    }

    this.qualityCollector = createCallQualityCollector(call, {
      onWarning: (name) => {
        this.handleTwilioQualityWarning(name);
        this.emitTwilioDialerEvent("call_quality_warning", {
          warning_name: name,
        });
      },
      onWarningCleared: (name, activeWarnings) => {
        this.handleTwilioQualityWarningCleared(name, activeWarnings);
        this.emitTwilioDialerEvent("call_quality_warning_cleared", {
          warning_name: name,
        });
      },
      onReconnecting: (error) => {
        this.setTwilioFeedback("Reconnecting call...", "warning");
        this.emitTwilioDialerEvent(
          "call_reconnecting",
          this.twilioErrorProps(error),
        );
      },
      onReconnected: () => {
        this.setTwilioFeedback("Call connection restored.", "info");
        this.emitTwilioDialerEvent("call_reconnected");
      },
    });
    this.qualityCollector.attach();

    call.on("accept", () => {
      this.startTimer();
      this.emitTwilioDialerEvent("call_accepted");
      // callConnected gates Drop VM: only an ANSWERED call may drop voicemail.
      this.setState({
        callLog: "Connected...",
        onPhone: true,
        callAnswered: true,
        callConnected: true,
      });
      // Ensure recordings are loaded by the time Drop VM becomes visible. The
      // mount-time fetch is skipped for ineligible users and may also be skipped
      // if PostHog flags weren't ready yet — re-fetch on connect when eligible.
      if (!this.state.vmRecordingsLoaded) {
        this.loadVoicemailRecordings();
      }
    });

    call.on("ringing", (hasEarlyMedia) => {
      this.emitTwilioDialerEvent("call_ringing", {
        has_early_media: !!hasEarlyMedia,
      });
      this.setState({ callLog: "Calling..." });
    });

    call.on("disconnect", () => {
      // A drop-initiated teardown (handleDropAccepted, post-resolve) is
      // caller-driven, not a prospect hangup — report it distinctly. If a drop
      // is still PENDING (disconnect raced ahead of the POST), DEFER the cause
      // so the resolution decides it (accepted => DROP_DISCONNECT; failed =>
      // REMOTE_DISCONNECT) rather than prematurely logging a remote hangup.
      if (this.dropInitiatedDisconnect) {
        this.dropInitiatedDisconnect = false;
        this.emitCallQualityEvent(DISCONNECT_CAUSES.DROP_DISCONNECT);
      } else {
        this.emitOrDeferQualityCause(DISCONNECT_CAUSES.REMOTE_DISCONNECT);
      }
      this.stopTimer();
      this.setState({ callConnected: false });
      this.handleCallEnd();
    });

    call.on("error", (error) => {
      console.error("Call error:", error);
      const cause =
        error && typeof error.code === "number"
          ? `${DISCONNECT_CAUSES.ERROR}:${error.code}`
          : DISCONNECT_CAUSES.ERROR;
      this.qualityCollector?.setSdkError(error);
      this.emitTwilioDialerEvent("call_error", this.twilioErrorProps(error));
      this.captureTwilioDiagnostic(error, "call_error");
      this.emitCallQualityEvent(cause);
      this.setState({
        callLog: `Error: ${error.message}`,
        onPhone: false,
        callConnected: false,
        twilioFeedback: "Call failed. Start a new call if needed.",
        twilioFeedbackLevel: "danger",
      });
      // Force the call to disconnect so the SDK fires call.on('disconnect'),
      // which routes through handleCallEnd via the normal path. Some
      // pre-accept errors don't auto-disconnect, leaving the call orphaned.
      if (call && typeof call.disconnect === "function") {
        try {
          call.disconnect();
        } catch (e) {
          console.warn("call.disconnect failed:", e);
        }
      }
    });
  };

  // Emit a disconnect quality cause, UNLESS a voicemail drop is still in flight:
  // then defer (keep the collector attached) so the drop's resolution can decide
  // the cause (accepted => DROP_DISCONNECT; failed => the deferred base cause).
  // Idempotent: once emitted (collector nulled) or already deferred, does nothing.
  emitOrDeferQualityCause(baseCause) {
    if (!this.qualityCollector) {
      return;
    }
    if (this.voicemailDropPending) {
      if (!this.qualityCauseDeferred) {
        this.qualityCauseDeferred = true;
        this.deferredQualityCause = baseCause;
      }
      return;
    }
    this.emitCallQualityEvent(baseCause);
  }

  // Flush a deferred disconnect cause once the drop resolves. `override` (e.g.
  // DROP_DISCONNECT on accept) takes precedence over the stored base cause.
  flushDeferredQualityCause(override = null) {
    if (!this.qualityCauseDeferred) {
      return;
    }
    const cause = override || this.deferredQualityCause;
    this.qualityCauseDeferred = false;
    this.deferredQualityCause = null;
    this.emitCallQualityEvent(cause);
  }

  emitCallQualityEvent = (disconnectCause) => {
    if (!this.qualityCollector) {
      return;
    }

    let effectiveConstraints = null;
    let micLabel = null;
    try {
      const audioTrack = this.connection
        ?.getLocalStream?.()
        ?.getAudioTracks?.()[0];
      effectiveConstraints = audioTrack?.getSettings?.() ?? null;
      micLabel = audioTrack?.label || null;
    } catch (e) {
      /* ignore */
    }

    let speakerLabel = null;
    try {
      const speakers = Array.from(
        this.device?.audio?.speakerDevices?.get?.() ?? [],
      );
      speakerLabel =
        speakers
          .map((d) => d.label)
          .filter(Boolean)
          .join(", ") || null;
    } catch (e) {
      /* ignore */
    }

    this.qualityCollector.setDisconnectCause(disconnectCause);
    this.qualityCollector.emit({
      direction: "outgoing",
      dialAttemptId: this.currentDialAttemptId || this.state.dialAttemptId,
      callStatus: this.getCallStatus(),
      answered: this.state.callAnswered,
      activityCreationAttempted: this.activityCreationAttempted,
      effectiveConstraints,
      micLabel,
      speakerLabel,
      requestedEdge: REQUESTED_EDGE,
      actualEdge: this.device ? this.device.edge : null,
    });

    this.qualityCollector.detach();
    this.qualityCollector = null;
  };

  detectDeviceType() {
    if (typeof window === "undefined") {
      return "desktop";
    }

    const isMobileSize = window.matchMedia("(max-width: 767px)").matches;
    const isTabletSize = window.matchMedia(
      "(min-width: 768px) and (max-width: 1368px)",
    ).matches;

    if (isMobileSize) {
      return "mobile";
    }

    if (isTabletSize) {
      return "tablet";
    }

    return "desktop";
  }

  async initializeDevice() {
    try {
      const token = await getAccessToken();

      this.device = new Device(token, DEVICE_OPTIONS);

      await this.device.register();

      // Check device state after registration attempt
      if (this.device.state === "registered") {
        this.emitTwilioDialerEvent("device_registered");
        const { userPhoneNumber, recordCall, userId, teamId } = this.state;
        const { leadPhone, lead } = this.props;

        this.setState({
          callLog: userPhoneNumber ? "Pending..." : "",
          onPhone: !!userPhoneNumber,
          initialized: true,
          error: "",
        });

        // Call handleTwilioEvents to set up the device and connection events
        this.handleTwilioEvents();

        if (userPhoneNumber && !this.connection && !this.isConnecting) {
          this.initiateConnection(
            leadPhone,
            userPhoneNumber,
            recordCall,
            userId,
            teamId,
            lead.id,
          );
        }
      }

      this.device.on(Device.EventName.Unregistered, () => {
        this.emitTwilioDialerEvent("device_unregistered");
        this.setState({
          initialized: false,
          onPhone: false,
          callConnected: false,
        });
      });

      this.device.on(Device.EventName.TokenWillExpire, async () => {
        this.emitTwilioDialerEvent("device_token_will_expire");
        try {
          const newToken = await getAccessToken();
          this.device.updateToken(newToken);
          this.emitTwilioDialerEvent("device_token_refreshed");
        } catch (error) {
          this.emitTwilioDialerEvent("device_token_refresh_failed", {
            twilio_error_message: error?.message || null,
          });
          this.captureTwilioDiagnostic(error, "device_token_refresh_failed");
          this.setState({ error: "Token update failed" });
        }
      });

      this.device.on(Device.EventName.Error, (error) => {
        console.error(
          "Device encountered an error during registration:",
          error,
        );
        this.emitTwilioDialerEvent(
          "device_error",
          this.twilioErrorProps(error),
        );
        this.setState({ error: error.message });
        this.handleConnectionError(error, "device_error");
      });
    } catch (error) {
      console.error("Error initializing device:", error);
      this.emitTwilioDialerEvent(
        "device_initialization_failed",
        this.twilioErrorProps(error),
      );
      this.captureTwilioDiagnostic(error, "device_initialization_failed");
      this.setState({
        error: "Device initialization failed. Please try again.",
      });
    }
  }

  async initiateConnection(
    leadPhone,
    userPhoneNumber,
    recordCall,
    userId,
    teamId,
    leadId,
  ) {
    try {
      const dialAttemptId = this.generateDialAttemptId();
      this.currentDialAttemptId = dialAttemptId;
      this.activityCreationAttempted = false;
      this.isConnecting = true;
      this.setState({
        dialAttemptId,
        callAnswered: false,
        twilioFeedback: null,
        twilioFeedbackLevel: "info",
      });
      this.emitTwilioDialerEvent("connect_requested", {
        dial_attempt_id: dialAttemptId,
        requested_edge: REQUESTED_EDGE,
      });

      const call = await this.device.connect({
        params: {
          To: leadPhone,
          From: userPhoneNumber,
          recordCall,
          user_id: userId,
          team_id: teamId,
          lead_id: leadId,
          attempt_id: dialAttemptId,
        },
      });

      if (call && typeof call.on === "function") {
        this.connection = call;
        this.isConnecting = false;
        this.handleCallEvents(call);
      } else {
        throw new Error("Invalid call object returned. Connection failed.");
      }
    } catch (error) {
      this.isConnecting = false;
      console.error("Error initiating call:", error);
      this.emitTwilioDialerEvent(
        "connect_failed",
        this.twilioErrorProps(error),
      );
      this.captureTwilioDiagnostic(error, "connect_failed");
      this.setState({
        callLog: `Error: ${error.message}`,
        onPhone: false,
        twilioFeedback: "Call failed. Start a new call if needed.",
        twilioFeedbackLevel: "danger",
      });
    }
  }

  // Create activity after disconnection.
  // Accepts an optional { callSid, callStatus } snapshot so it can run after
  // `this.connection` has been nulled (e.g. a call that disconnected mid-drop,
  // then the drop failed). When no snapshot is passed it reads the live
  // connection — the normal hangup path — and behaves exactly as before.
  createActivityAfterDisconnection(snapshot = null) {
    const { body } = this.state;
    const { lead } = this.props;
    const { setDialerField, switchForm, currentForm } = this.props;
    const callSid = snapshot
      ? snapshot.callSid
      : this.connection.parameters.CallSid;
    const callStatus = snapshot
      ? snapshot.callStatus
      : this.connection.status();
    this.setState({ creatingActivity: true });

    const data = {
      event: "phone_call",
      dial_attempt_id: this.currentDialAttemptId || this.state.dialAttemptId,
      activity: {
        body,
        call_sid: callSid,
        internal: true,
      },
    };

    if (callStatus) {
      data.activity.parameters = {
        call_disposition: callStatus,
      };
    }

    pendingActivityRequest = APIRequest.post({
      resource: `/v1/leads/${lead.id}/activities`,
      data,
    });

    pendingActivityRequest.end((error, response) => {
      if (this._isMounted) {
        this.setState({ creatingActivity: false });
      }

      if (!error) {
        const { id } = response.body;

        this.dismissDialerModal();

        AppDispatcher.dispatch({
          type: "CREATE_LEAD_ACTIVITY_DONE",
        });

        setDialerField({
          id,
          body,
          callSid,
          callStatus,
        });

        if (switchForm && currentForm !== "phone-call") {
          switchForm("phone-call", false);
        }
      } else {
        this.emitTwilioDialerEvent("activity_create_failed", {
          call_status: callStatus || null,
        });
        this.captureTwilioDiagnostic(error, "activity_create_failed", {
          call_status: callStatus || null,
        });
      }
    });
  }

  sendDigit(digit) {
    // DTMF requires an established media connection. Before the call is
    // accepted (or after it has ended) the Twilio Call has no RTCPeerConnection
    // and sendDigits() throws inside the SDK.
    const callStatus = this.getCallStatus();
    if (callStatus !== "open") {
      this.emitTwilioDialerEvent("dtmf_digit_skipped", {
        call_status: callStatus,
      });
      return;
    }

    try {
      this.setLastUiAction("dtmf_digit_sent");
      this.connection.sendDigits(digit);
    } catch (error) {
      console.error("Error sending digit:", error);
      this.captureTwilioDiagnostic(error, "dtmf_digit_failed");
    }
  }

  renderDigitButton = (button, disabled = false) => (
    <div
      key={button.digit}
      className="digit tablet-cancel-draggable"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => this.sendDigit(button.digit)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (!disabled) {
            this.sendDigit(button.digit);
          }
        }
      }}
    >
      {button.digit}
      {button.subDigit && <div className="sub-digit">{button.subDigit}</div>}
    </div>
  );

  renderDTMFTone = (disabled = false) => (
    <div className="col-12">
      {BUTTONS.map((row) => (
        <div
          key={`row-${row.map((button) => button.digit).join("")}`}
          className="row"
        >
          {row.map((button) => this.renderDigitButton(button, disabled))}
        </div>
      ))}
    </div>
  );

  renderMuteButton() {
    const { muted } = this.state;

    return (
      <button
        type="button"
        className="btn btn-default mr-2 tablet-cancel-draggable"
        onClick={this.handleToggleMute}
        aria-label={muted ? "Unmute microphone" : "Mute microphone"}
        data-testid="dialer-mute-button"
      >
        <FontAwesomeIcon
          icon={["fas", `${muted ? "fa-microphone-slash" : "fa-microphone"}`]}
        />
      </button>
    );
  }

  renderHoldButton() {
    const { hold } = this.state;

    return (
      <button
        type="button"
        className="btn btn-default mr-2 tablet-cancel-draggable"
        onClick={this.handleToggleHold}
        aria-label={hold ? "Resume call" : "Hold call"}
        data-testid="dialer-hold-button"
      >
        <FontAwesomeIcon icon={["fas", `${hold ? "fa-play" : "fa-pause"}`]} />
      </button>
    );
  }

  renderCallToggleButton() {
    const { onPhone, initialized } = this.state;

    return (
      <button
        type="button"
        className={`btn ${
          onPhone ? "btn-danger" : "btn-success"
        } tablet-cancel-draggable`}
        disabled={!initialized}
        onClick={this.handleToggleCall}
        aria-label={onPhone ? "End call" : "Start call"}
        data-testid={onPhone ? "dialer-hangup-button" : "dialer-call-button"}
      >
        <FontAwesomeIcon icon={["fas", "fa-phone-alt"]} />
      </button>
    );
  }

  // Drop VM footer control (mockup states 2-4 + submitting). Rendered between
  // the hold button and the red hangup button for eligible callers. Before the
  // outbound leg is accepted, show the affordance but keep it inactive; after
  // accept, render the actionable/no-recordings states.
  renderDropVmControl() {
    const { vmRecordings, vmRecordingsLoaded, dropStatus, vmDropdownOpen } =
      this.state;

    if (!this.voicemailDropEligible()) {
      return null;
    }

    const submitting = dropStatus === "dropping";
    const label = submitting ? VM_COPY.dropping : "Drop VM";

    if (!this.voicemailDropAvailable()) {
      return (
        <Tooltipable
          text={VM_COPY.preconnect}
          placement="top"
          className="d-inline-block"
        >
          <button
            type="button"
            className="btn btn-secondary tablet-cancel-draggable text-muted"
            style={{ opacity: 0.65, cursor: "not-allowed" }}
            aria-disabled="true"
            data-testid="voicemail-drop-preconnect-disabled"
            onClick={(e) => e.preventDefault()}
          >
            <FontAwesomeIcon icon={["fas", "fa-voicemail"]} className="mr5" />
            Drop VM
          </button>
        </Tooltipable>
      );
    }

    // State 2 — no recordings: focusable aria-disabled grey control + tooltip,
    // plus a compact visible Settings link for touch/unreliable-tooltip layouts.
    if (vmRecordingsLoaded && vmRecordings.length === 0) {
      return (
        <span
          className="d-inline-flex align-items-center"
          data-testid="voicemail-drop-no-recordings"
        >
          <Tooltipable text={VM_COPY.noRecordings} placement="top">
            {/*
              Greyed but NOT `.btn.disabled`: Bootstrap 4 sets
              `pointer-events:none` on `.btn.disabled`, which suppresses the
              hover/focus tooltip. Keep it focusable (real button, no `disabled`
              attribute) + aria-disabled so the guidance tooltip and the visible
              Set-up link stay discoverable for keyboard/touch users.
            */}
            <button
              type="button"
              className="btn btn-secondary tablet-cancel-draggable text-muted"
              style={{ opacity: 0.65, cursor: "not-allowed" }}
              aria-disabled="true"
              data-testid="voicemail-drop-disabled"
              onClick={(e) => e.preventDefault()}
            >
              <FontAwesomeIcon icon={["fas", "fa-voicemail"]} className="mr5" />
              Drop VM
            </button>
          </Tooltipable>
          <a
            href={SETTINGS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="ml5 small tablet-cancel-draggable"
            data-testid="voicemail-drop-settings-link"
          >
            Set up
          </a>
        </span>
      );
    }

    // Still loading recordings — render nothing rather than a misleading state.
    if (!vmRecordingsLoaded) {
      return null;
    }

    // State 3 — single default recording: one green Drop VM button.
    if (vmRecordings.length === 1) {
      return (
        <Tooltipable
          text={VM_COPY.beepTooltip}
          placement="top"
          className="d-inline-block"
        >
          <button
            type="button"
            className="btn btn-success tablet-cancel-draggable"
            disabled={submitting}
            onClick={this.handleDropDefault}
            data-testid="voicemail-drop-button"
          >
            <FontAwesomeIcon icon={["fas", "fa-voicemail"]} className="mr5" />
            {label}
          </button>
        </Tooltipable>
      );
    }

    // State 4 — multiple recordings: split button (primary drops default,
    // chevron opens a dropdown of recordings).
    return (
      <span
        className={`btn-group dropup ${vmDropdownOpen ? "show" : ""}`}
        role="group"
        data-testid="voicemail-drop-split"
      >
        <Tooltipable
          text={VM_COPY.beepTooltip}
          placement="top"
          className="d-inline-block"
        >
          <button
            type="button"
            className="btn btn-success tablet-cancel-draggable"
            disabled={submitting}
            onClick={this.handleDropDefault}
            data-testid="voicemail-drop-button"
          >
            <FontAwesomeIcon icon={["fas", "fa-voicemail"]} className="mr5" />
            {label}
          </button>
        </Tooltipable>
        <button
          type="button"
          className={`btn btn-success dropdown-toggle dropdown-toggle-split tablet-cancel-draggable ${vmDropdownOpen ? "show" : ""}`}
          aria-haspopup="true"
          aria-expanded={vmDropdownOpen}
          disabled={submitting}
          onClick={this.toggleVmDropdown}
          data-testid="voicemail-drop-toggle"
        >
          <span className="sr-only">Choose voicemail drop recording</span>
        </button>
        <div
          className={`dropdown-menu dropdown-menu-right ${vmDropdownOpen ? "show" : ""}`}
          data-testid="voicemail-drop-menu"
        >
          {vmRecordings.map((recording) => (
            <a
              key={recording.id}
              href="#"
              className="dropdown-item tablet-cancel-draggable"
              data-testid={`voicemail-drop-option-${recording.id}`}
              onClick={(e) => {
                e.preventDefault();
                this.handleDropRecording(recording);
              }}
            >
              {recording.name}
              {recording.is_default && (
                <span className="badge badge-green ml5">Default</span>
              )}
              <span className="text-muted small ml5">
                {formatDuration(recording.duration_seconds)}
              </span>
            </a>
          ))}
        </div>
      </span>
    );
  }

  render() {
    const {
      error,
      onPhone,
      callLog,
      initialized,
      timerTime,
      timerOn,
      userPhoneNumber,
      creatingActivity,
      disconnectionHandled,
      twilioFeedback,
      twilioFeedbackLevel,
      dropStatus,
      body,
      callConnected,
    } = this.state;
    const { containerID, modalClass, dialogClass, lead, leadPhone } =
      this.props;

    const dropAccepted = dropStatus === "accepted";
    const dropInFlight = dropStatus === "dropping";
    // DTMF only works once the call is answered and media is established.
    const dialpadDisabled = dropAccepted || !callConnected;
    const notesValue = (body || "").replace(/<br \/>/g, "\n");

    // Prevent render if disconnected and `disconnectionHandled` is set —
    // EXCEPT while a voicemail drop is IN FLIGHT or has been ACCEPTED. If the
    // callee disconnects mid-drop, handleDisconnection sets initialized=false;
    // unmounting here would tear down <AppModal>, whose modal('hide') unmounts
    // the whole Dialer (_isMounted=false) so the in-flight drop's resolution
    // (accepted UI, or the deferred-activity creation on failure) would never
    // run and the call activity would be LOST. Stay mounted until the drop
    // resolves; on failure the component can then unmount normally.
    if (
      (!initialized || disconnectionHandled) &&
      !dropAccepted &&
      !dropInFlight
    ) {
      return null;
    }

    // After a mid-drop disconnect the call controls are gone but the drop is
    // still resolving — show a minimal "Dropping..." state instead of crashing
    // on a null connection / onPhone=false.
    const droppingAfterDisconnect = dropInFlight && !onPhone;
    const dropVmControl =
      onPhone && this.connection && !dropAccepted
        ? this.renderDropVmControl()
        : null;

    const seconds = `0${Math.floor(timerTime / 1000) % 60}`.slice(-2);
    const minutes = `0${Math.floor(timerTime / 60000) % 60}`.slice(-2);
    const hours = `0${Math.floor(timerTime / 3600000)}`.slice(-2);

    const draggableConfig = this.getDraggableConfig();

    return (
      <AppModal
        containerID={containerID}
        modalClass={modalClass}
        dialogClass={dialogClass}
        contentClass=""
        ref={(appModal) => (this.appModal = appModal)}
        onHide={this.handleModalHide}
      >
        <Draggable {...draggableConfig}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="appModalLabel">
                {LeadHelpers.renderLeadName(lead)}
                <br />
                {LeadHelpers.renderPhoneNumber(leadPhone)}
              </h5>

              {!creatingActivity && (
                // Block closing the dialer while a drop is in flight: unmounting
                // mid-request sets _isMounted=false, so the drop's resolution
                // (accepted UI, or handleDropFailed creating the deferred
                // activity) would never run and the call would vanish. Drop the
                // data-dismiss + disable the X while dropping; it re-enables in
                // the accepted/failed states once the drop resolves.
                <button
                  type="button"
                  className={`close tablet-cancel-draggable ${dropInFlight ? "disabled" : ""}`}
                  {...(dropInFlight ? {} : { "data-dismiss": "modal" })}
                  disabled={dropInFlight}
                  aria-disabled={dropInFlight || undefined}
                  aria-label="Close"
                  data-testid="dialer-close"
                  onClick={this.handleDialerCloseClick}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              )}
            </div>

            <div className="modal-body">
              <div className="container">
                <div className="row">
                  {creatingActivity && (
                    <div className="col-12 text-center">
                      <FontAwesomeIcon
                        icon="far fa-spinner"
                        pulse
                        className="mr5"
                      />{" "}
                      Saving note ...
                    </div>
                  )}

                  {error && (
                    <div className="col-12 text-center text-danger">
                      {error}
                    </div>
                  )}

                  {!userPhoneNumber && (
                    <div className="col-12 text-center text-danger">
                      Please Set Up Phone Number on the Phone Number Page{" "}
                      <a
                        href="/users/phone_number/edit"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-danger tablet-cancel-draggable"
                      >
                        here
                      </a>
                    </div>
                  )}

                  {twilioFeedback &&
                    !dropAccepted &&
                    !droppingAfterDisconnect && (
                      <div
                        className={`col-12 text-center text-${twilioFeedbackLevel} mt5`}
                      >
                        {twilioFeedback}
                      </div>
                    )}

                  {dropAccepted && (
                    <>
                      <div className="col-12 text-center font-weight-bold">
                        {VM_COPY.playingHeader}
                      </div>
                      <div className="col-12 text-center text-muted">
                        {VM_COPY.callerDisconnected}
                      </div>
                      <div
                        className="col-12 mt15 alert alert-success text-center"
                        role="status"
                        data-testid="voicemail-drop-accepted-notice"
                      >
                        {VM_COPY.accepted}
                      </div>
                    </>
                  )}

                  {droppingAfterDisconnect && (
                    <div
                      className="col-12 text-center"
                      role="status"
                      data-testid="voicemail-drop-in-flight"
                    >
                      <FontAwesomeIcon
                        icon="far fa-spinner"
                        pulse
                        className="mr5"
                      />{" "}
                      {VM_COPY.dropping}
                    </div>
                  )}

                  {!dropAccepted &&
                    !droppingAfterDisconnect &&
                    !error &&
                    callLog &&
                    userPhoneNumber && (
                      <>
                        <div className="col-12 text-center">
                          {!creatingActivity && callLog}
                        </div>
                        {timerOn && (
                          <div className="col-12 text-center">
                            {hours > 0 && `${hours} : `}
                            {minutes} : {seconds}
                          </div>
                        )}
                      </>
                    )}

                  {(onPhone || dropAccepted) && (
                    <>
                      <div
                        className={
                          dropAccepted ? "voicemail-drop-dialpad-disabled" : ""
                        }
                        style={
                          dialpadDisabled
                            ? { opacity: 0.5, pointerEvents: "none" }
                            : undefined
                        }
                        aria-disabled={dialpadDisabled || undefined}
                        data-testid="dialer-dialpad"
                      >
                        {this.renderDTMFTone(dialpadDisabled)}
                      </div>

                      <div className="col-12 mt15">
                        <label htmlFor="notes" className="d-block">
                          Notes
                        </label>
                        <textarea
                          id="notes"
                          className="form-control tablet-cancel-draggable"
                          rows="4"
                          // Freeze from the moment the drop is submitted: the
                          // body was already sent with the POST and the accepted
                          // path suppresses the browser-side save, so any edit
                          // made while dropping or after accept is silently lost.
                          // Keep the typed value visible.
                          readOnly={dropInFlight || dropAccepted}
                          aria-readonly={
                            dropInFlight || dropAccepted || undefined
                          }
                          defaultValue={notesValue}
                          onChange={(v) => {
                            if (dropInFlight || dropAccepted) {
                              return;
                            }
                            this.state.body = v.target.value.replace(
                              /(?:\r\n|\r|\n)/g,
                              "<br />",
                            );
                          }}
                        ></textarea>
                      </div>
                    </>
                  )}

                  {dropStatus === "failed" && this.state.dropError && (
                    <div
                      className="col-12 mt15 alert alert-danger text-center"
                      role="alert"
                      data-testid="voicemail-drop-failure-notice"
                    >
                      {this.state.dropError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="col-12 text-center">
                {onPhone &&
                this.connection &&
                !dropAccepted &&
                dropVmControl ? (
                  <div
                    className="d-flex flex-column align-items-center w-100"
                    data-testid="voicemail-drop-two-row-footer"
                  >
                    <div
                      className="d-flex justify-content-center align-items-center"
                      data-testid="voicemail-drop-call-control-row"
                    >
                      {this.renderMuteButton()}
                      {this.renderHoldButton()}
                      {!creatingActivity && this.renderCallToggleButton()}
                    </div>
                    <div
                      className="d-flex justify-content-center align-items-center mt-2"
                      data-testid="voicemail-drop-action-row"
                    >
                      {dropVmControl}
                    </div>
                  </div>
                ) : (
                  <>
                    {onPhone && this.connection && !dropAccepted && (
                      <>
                        {this.renderMuteButton()}
                        {this.renderHoldButton()}
                      </>
                    )}

                    {!creatingActivity &&
                      !dropAccepted &&
                      this.renderCallToggleButton()}
                  </>
                )}
              </div>
            </div>
          </div>
        </Draggable>
      </AppModal>
    );
  }
}

Dialer.defaultProps = {
  containerID: "secondary-modal",
  modalClass: "modal modal-overlay",
  dialogClass: "modal-dialog modal-sm",
  currentForm: null,
  phoneSlot: null,
  switchForm: NOOP_FALSE,
  setDialerField: NOOP_FALSE,
  onDialerClose: NOOP,
};

Dialer.propTypes = {
  containerID: PropTypes.string,
  modalClass: PropTypes.string,
  dialogClass: PropTypes.string,
  lead: PropTypes.shape({}).isRequired,
  leadPhone: PropTypes.string.isRequired,
  currentForm: PropTypes.string,
  phoneSlot: PropTypes.string,
  switchForm: PropTypes.func,
  setDialerField: PropTypes.func,
  onDialerClose: PropTypes.func,
};

export default Dialer;
